/**
 * Maths project covers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_project_pages`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const mathsProjectCoversPack: ToilePack = {
    slug: 'maths-project-covers',
    category: 'classroom',
    name: 'Maths project covers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'numbers', label: 'Numbers', subject: 'a cover with numbers and symbols around "MATHS PROJECT"'},
        {key: 'geometry', label: 'Geometry', subject: 'a cover with compasses, protractors and shapes'},
        {key: 'pi', label: 'Pi', subject: 'a cover with a large pi symbol and formulas'},
    ],
    styles: [
        {key: 'sketch', label: 'Sketch pens', style: 'outlined in black fineliner and filled in with bright sketch pens in flat even colour, neat hand-drawn lines with a slight wobble', palette: 'bright reds, yellows, blues and greens', framing: 'centred on a white A4 sheet seen straight on with an even clean margin all round'},
        {key: 'pencil', label: 'Colour pencils', style: 'drawn in sharp colour pencils with soft layered shading and visible pencil strokes, a gentle hand-coloured finish', palette: 'soft pencil tones of orange, teal and violet', framing: 'filling a white A4 sheet seen straight on, a narrow pencil-ruled margin line inside the edge of the page'},
        {key: 'duo', label: 'Two-colour pen', style: 'drawn in black gel pen with one accent colour of highlighter, clean ink lines, minimal and tidy', palette: 'black ink with yellow highlighter only', framing: 'on a cream notebook page seen straight on with a generous margin'},
    ],
    directions: ['fractions', 'graph paper', 'cartoon'],
    directionHint: 'try: fractions',
    seo: {
        title: 'Maths project covers: numbers, geometry, pi',
        metaDescription: 'Nine hand-drawn maths project covers: numbers, geometry and pi designs, in three pen styles for a project file.',
        h1: 'Maths project covers: numbers, geometry, pi',
        tagline: 'Nine covers: numbers, geometry and pi, each in three pen styles.',
        intro: 'Front pages for a maths project file, drawn by hand with pens and colours. The numbers cover has numbers and symbols around the words MATHS PROJECT. The geometry cover has compasses, protractors and shapes. The pi cover has a large pi symbol and formulas. Each comes in three pen styles. Type a twist, such as fractions, graph paper or cartoon, and the same three subjects can take it.',
        useCases: [
            'a maths project for school',
            'a geometry project file',
            'a numbers or formulas workbook',
        ],
        faq: [
            {q: 'What is on the geometry cover?', a: 'Compasses, protractors and shapes.'},
            {q: 'What is on the pi cover?', a: 'A large pi symbol with formulas around it.'},
            {q: 'What are the three styles?', a: 'Sketch pens use bright flat colour inside black fineliner outlines. Colour pencils give soft layered shading in pastel tones. Two-colour pen is black gel ink with one highlighter colour.'},
            {q: 'Can I ask for fractions or graph paper?', a: 'Yes. Type it as a twist and it is a request for the same layouts, not a promised result.'},
        ],
    },
}
